/*global ViewController, WebView*/

/**
 * The tickets filed from the composer, newest first.
 *
 * iz_freshdesk_ticket has been written on every send since the feature shipped and never read
 * back, so there was no way to find a ticket one had opened.
 *
 * NOT scoped to "mine", on purpose: every consultant reaches this through the single shared
 * `adm` login, so sent_by_uid names nobody -- requester_upn is the only thing that does. And
 * the point of a shared helpdesk is to see that a colleague already reported the thing you are
 * about to report, which a per-user list would hide.
 */
var freshdeskTicketHistoryController = new ViewController(
    {
        directory: "views/freshdesk",
        name: "freshdeskTicketHistory",
        route: "fd-ticket-history",
        openInPopup: true,
        //the large popup body is otherwise fixed at the screen's height : the class lets the
        //view's stylesheet size it to the list (see freshdeskTicketHistory.scss)
        popup: { size: "large", dialogClass: "fd-hist-dialog" }
    }
) ;

/**
 * One page is what the popup shows without scrolling, and what one live read of Freshdesk
 * covers (izFreshdesk.tickets reads at most this many).
 */
var FD_PAGE_SIZE = 15 ;
//typing in the search box waits for a pause before asking Freshdesk anything : every call
//costs up to 16 requests on an account limited to about 100 a minute, shared by all support
var FD_SEARCH_PAUSE_MS = 400 ;

/**
 * A readable stamp rather than the raw column: the row is scanned, not parsed.
 */
function fdWhen(value){
    if(!value){ return "" ; }
    var d = new Date(value) ;
    if(isNaN(d.getTime())){ return String(value) ; }
    var two = function(n){ return (n < 10 ? "0" : "") + n ; } ;
    return two(d.getDate()) + "/" + two(d.getMonth() + 1) + "/" + d.getFullYear() +
        " " + two(d.getHours()) + ":" + two(d.getMinutes()) ;
}

freshdeskTicketHistoryController.prepareDataOnEnter = function(data, callback){
    //The helpdesk root comes from its own endpoint rather than from `options`: options calls
    //Freshdesk and fails with it, and a list of links has no business going dark because the
    //helpdesk is having a bad minute.
    this.api.izFreshdesk.helpdesk(function(errUrl, helpdesk){
        var root = (!errUrl && helpdesk && helpdesk.url) ? helpdesk.url : "" ;

        this.api.db.multiread({
            iz_freshdesk_ticket: {
                search: {},
                orderBy: "created_at desc"
            }
        }, function(err, reads){
            if(err){ return callback(err) ; }
            var rows = (reads && reads.iz_freshdesk_ticket) || [] ;
            //the whole list stays here ; the view gets the page the filters leave on screen
            this._all = rows.map(function(t){
                return {
                    id: t.fd_ticket_id,
                    subject: t.subject || "",
                    //the display name when Office 365 proved one, the address otherwise
                    who: t.requester_name || t.requester_upn || "",
                    verified: !!t.requester_verified,
                    when: fdWhen(t.created_at),
                    stamp: new Date(t.created_at).getTime() || 0,
                    url: root ? (root + "/helpdesk/tickets/" + t.fd_ticket_id) : ""
                } ;
            }) ;
            //status and content are read live, page by page, and kept for the popup's life
            this._live = {} ;
            //ids asked for and not answered yet : a second refresh must not ask for them again
            this._pending = {} ;
            var people = this._all.map(function(t){ return t.who ; }).filter(function(w, i, all){
                return w && all.indexOf(w) === i ;
            }).sort() ;
            var view = {
                //no root, no link : an anchor with an empty href looks clickable and goes
                //nowhere, which is worse than plain text
                hasLinks: !!root,
                total: this._all.length,
                search: "",
                sort: "dateDesc",
                who: "",
                people: [{ value: "", label: WebView.tr("freshdesk.historyEveryone") }].concat(people.map(function(p){ return { value: p, label: p } ; })),
                page: 1,
                pages: 1,
                matching: 0,
                openId: null,
                tickets: []
            } ;
            this._paginate(view) ;
            callback(null, view) ;
            this._loadLive() ;
        }.bind(this)) ;
    }.bind(this)) ;
} ;

/**
 * Filter, sort and cut the page. Search covers what a consultant remembers of a ticket: words of
 * its subject, its number, who filed it.
 */
freshdeskTicketHistoryController._paginate = function(data){
    const needle = String(data.search || "").trim().toLowerCase().replace(/^#/, "") ;
    let rows = (this._all || []).filter((t)=>{
        if(data.who && t.who !== data.who){ return false ; }
        if(!needle){ return true ; }
        return t.subject.toLowerCase().indexOf(needle) !== -1 ||
            String(t.id).indexOf(needle) !== -1 ||
            t.who.toLowerCase().indexOf(needle) !== -1 ;
    }) ;
    rows.sort((a, b)=>{
        if(data.sort === "dateAsc"){ return a.stamp - b.stamp ; }
        if(data.sort === "who"){ return a.who.localeCompare(b.who) || b.stamp - a.stamp ; }
        return b.stamp - a.stamp ;
    }) ;
    data.matching = rows.length ;
    data.pages = Math.max(1, Math.ceil(rows.length / FD_PAGE_SIZE)) ;
    data.page = Math.min(Math.max(1, data.page || 1), data.pages) ;
    const start = (data.page - 1) * FD_PAGE_SIZE ;
    const live = this._live || {} ;
    data.tickets = rows.slice(start, start + FD_PAGE_SIZE).map((t)=>{
        const read = live[t.id] ;
        return Object.assign({}, t, {
            open: data.openId === t.id,
            loaded: !!read,
            statusLabel: read ? read.statusLabel : "",
            //Freshdesk numbers : 4 resolved, 5 closed -- done, so greyed rather than coloured
            statusDone: !!read && (read.status === 4 || read.status === 5),
            description: read ? read.description : ""
        }) ;
    }) ;
    data.hasPrev = data.page > 1 ;
    data.hasNext = data.page < data.pages ;
} ;

/**
 * One call for the page on screen. A helpdesk that does not answer leaves the rows as they
 * were : the list itself never depended on it.
 */
freshdeskTicketHistoryController._loadLive = function(){
    const data = this.data ;
    if(!data){ return ; }
    const live = this._live ;
    const pending = this._pending ;
    const missing = data.tickets.filter((t)=>{ return !live[t.id] && !pending[t.id] ; }).map((t)=>{ return t.id ; }) ;
    if(missing.length === 0){ return ; }
    missing.forEach((id)=>{ pending[id] = true ; }) ;
    this.api.izFreshdesk.tickets(missing, (err, result)=>{
        missing.forEach((id)=>{ delete pending[id] ; }) ;
        //a failed call is asked again on the next refresh : Freshdesk may be back by then
        if(err || !result || !Array.isArray(result.tickets)){ return ; }
        result.tickets.forEach((t)=>{ live[t.id] = t ; }) ;
        //a ticket Freshdesk no longer has (deleted, merged) is remembered as such, or every
        //refresh would ask for it again
        missing.forEach((id)=>{
            if(!live[id]){ live[id] = { id: id, status: null, statusLabel: "", description: "" } ; }
        }) ;
        if(this.data !== data){ return ; }      //closed or reopened meanwhile
        this._paginate(data) ;
        this.view.render() ;
    }) ;
} ;

freshdeskTicketHistoryController._refresh = function(){
    this.view.updateData() ;
    this._paginate(this.data) ;
    this.view.render() ;
    this._loadLive() ;
} ;

//the list filters on every keystroke -- it is local -- but Freshdesk is asked only once the
//consultant stops typing
freshdeskTicketHistoryController.onHistSearch = function(){
    this.view.updateData() ;
    this.data.page = 1 ;
    this._paginate(this.data) ;
    this.view.render() ;
    clearTimeout(this._searchTimer) ;
    this._searchTimer = setTimeout(()=>{ this._loadLive() ; }, FD_SEARCH_PAUSE_MS) ;
} ;

freshdeskTicketHistoryController.onHistSort = function(){
    this._refresh() ;
} ;

freshdeskTicketHistoryController.onHistWho = function(){
    this.view.updateData() ;
    this.data.page = 1 ;
    this._refresh() ;
} ;

freshdeskTicketHistoryController.onHistPrev = function(){
    this.data.page = this.data.page - 1 ;
    this._refresh() ;
} ;

freshdeskTicketHistoryController.onHistNext = function(){
    this.data.page = this.data.page + 1 ;
    this._refresh() ;
} ;

//a click on a row unfolds what support received ; a second click folds it back
freshdeskTicketHistoryController.onHistToggle = function(ev){
    //the emitter wraps what the view sent : the row is in ev.data, as in commentPopup's onDelete
    const sent = (ev && ev.data) || {} ;
    const row = sent.currentData ;
    if(!row){ return ; }
    //the link to Freshdesk sits inside the row : following it must not also fold the row
    const target = sent.originalEvent && sent.originalEvent.target ;
    if(target && target.closest && target.closest(".fd-hist-link")){ return ; }
    this.data.openId = this.data.openId === row.id ? null : row.id ;
    this._refresh() ;
} ;

module.exports = freshdeskTicketHistoryController ;
